import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertTriangle, Sparkles } from 'lucide-react';
import { BackButton } from '../components/BackButton';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'konnect_access_token';
const REFRESH_KEY = 'konnect_refresh_token';

/**
 * Magic-link landing page reached from the invite email.
 * Exchanges the invite_code for a JWT (no password needed) and routes the
 * user to the password-set screen, then to a quick orientation, then dashboard.
 */
export default function AcceptInvitePage() {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | error
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');
  const ranRef = useRef(false);

  useEffect(() => {
    // Prevent React StrictMode's double-effect from invalidating the invite twice
    if (ranRef.current) return;
    ranRef.current = true;
    if (!inviteCode) { setStatus('error'); setError('Missing invitation code in the URL.'); return; }

    (async () => {
      try {
        const { data } = await axios.post(`${API}/api/staff/accept-invite/${inviteCode}`);
        if (data && data.token) {
          // Store tokens so AuthContext picks them up on the next route
          localStorage.setItem(TOKEN_KEY, data.token);
          if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
          setUserName(data.full_name || data.email || '');
          // Brief success flash, then route to set-password
          setTimeout(() => {
            // Use full reload so AuthContext re-runs checkAuth with the new token
            window.location.assign('/set-password?welcome=1');
          }, 900);
        } else {
          setStatus('error');
          setError('Invitation accepted but no session was returned. Please contact your admin.');
        }
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || 'Something went wrong.';
        setStatus('error');
        setError(typeof detail === 'string' ? detail : 'Invitation could not be accepted.');
      }
    })();
  }, [inviteCode]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4 relative">
      <BackButton fallbackHref="/" testId="invite-back-btn" />
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-emerald-100 p-8 text-center" data-testid="accept-invite-card">
        {status === 'loading' && (
          <>
            <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-5 shadow-lg shadow-emerald-500/20">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900 mb-2">Welcome to Konnect</h1>
            <p className="text-sm text-slate-500 mb-6">Logging you in securely{userName ? `, ${userName.split(' ')[0]}` : ''}…</p>
            <div className="flex items-center justify-center gap-2 text-emerald-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm font-medium" data-testid="accept-invite-status">Verifying invitation</span>
            </div>
            {userName ? (
              <div className="mt-6 flex items-center justify-center gap-2 text-emerald-700">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-xs">Session created — taking you to set up your password…</span>
              </div>
            ) : null}
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mx-auto w-16 h-16 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center mb-5">
              <AlertTriangle className="h-8 w-8 text-rose-500" />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900 mb-2">Invitation Issue</h1>
            <p className="text-sm text-slate-600 mb-6" data-testid="accept-invite-error">{error}</p>
            <div className="flex flex-col gap-2">
              <Button asChild className="bg-emerald-600 hover:bg-emerald-500" data-testid="invite-go-to-login">
                <Link to="/login">Go to Login</Link>
              </Button>
              <p className="text-xs text-slate-400 mt-2">
                If you believe this link should work, ask your admin to resend the invitation.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
